import { useState } from 'react';
import { Player } from '@/mocks/roster';

interface PlayerCardProps {
  player: Player;
}

export default function PlayerCard({ player }: PlayerCardProps) {
  const [flipped, setFlipped] = useState(false);

  const positionColors: Record<string, string> = {
    QB: 'bg-[#ffd700] text-[#4a1a6b]',
    RB: 'bg-[#4a1a6b] text-white',
    WR: 'bg-[#4a1a6b] text-white',
    TE: 'bg-[#4a1a6b] text-white',
    LT: 'bg-[#2a0a3d] text-white',
    LG: 'bg-[#2a0a3d] text-white',
    C: 'bg-[#2a0a3d] text-white',
    RG: 'bg-[#2a0a3d] text-white',
    RT: 'bg-[#2a0a3d] text-white',
    DE: 'bg-[#6b2fa0] text-white',
    DT: 'bg-[#6b2fa0] text-white',
    LB: 'bg-[#6b2fa0] text-white',
    CB: 'bg-[#8b3fc0] text-white',
    S: 'bg-[#8b3fc0] text-white',
    K: 'bg-[#ffd700] text-[#4a1a6b]',
    P: 'bg-[#ffd700] text-[#4a1a6b]',
  };

  const badgeClass = positionColors[player.position] || 'bg-[#4a1a6b] text-white';

  return (
    <div
      className="relative cursor-pointer group"
      style={{ perspective: '1000px' }}
      onClick={() => setFlipped(!flipped)}
    >
      <div
        className="relative w-full transition-transform duration-500"
        style={{
          transformStyle: 'preserve-3d',
          transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
          minHeight: '380px',
        }}
      >
        {/* Front */}
        <div
          className="absolute inset-0 rounded-lg overflow-hidden bg-white border border-gray-100"
          style={{ backfaceVisibility: 'hidden' }}
        >
          <div className="relative h-56 overflow-hidden bg-gradient-to-b from-[#4a1a6b]/10 to-[#4a1a6b]/5">
            <img
              src={player.image}
              alt={player.name}
              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
            />
            {/* Number overlay */}
            <div className="absolute top-3 left-3">
              <span className="text-4xl font-black text-white/20 leading-none" style={{ fontFamily: "'Oswald', sans-serif" }}>
                #{player.number}
              </span>
            </div>
            {/* Position badge */}
            <div className="absolute top-3 right-3">
              <span className={`px-2 py-1 rounded-md text-xs font-black tracking-widest uppercase ${badgeClass}`} style={{ fontFamily: "'Oswald', sans-serif" }}>
                {player.position}
              </span>
            </div>
            {/* Gradient overlay */}
            <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent"></div>
          </div>

          <div className="px-4 pb-4 pt-2">
            <div className="flex items-start justify-between mb-1">
              <div>
                <h3 className="text-base font-black text-[#2a0a3d] leading-tight" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  {player.name}
                </h3>
                <p className="text-xs text-gray-500 tracking-wider uppercase" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  {player.position} · #{player.number}
                </p>
              </div>
              <div className="w-6 h-6 flex items-center justify-center text-gray-300 group-hover:text-[#4a1a6b] transition-colors">
                <i className="ri-refresh-line text-sm"></i>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 mt-3">
              <div className="bg-gray-50 rounded-md px-2 py-1.5">
                <p className="text-[10px] text-gray-400 uppercase tracking-wider" style={{ fontFamily: "'Oswald', sans-serif" }}>Height</p>
                <p className="text-xs font-bold text-[#2a0a3d]" style={{ fontFamily: "'Oswald', sans-serif" }}>{player.height}</p>
              </div>
              <div className="bg-gray-50 rounded-md px-2 py-1.5">
                <p className="text-[10px] text-gray-400 uppercase tracking-wider" style={{ fontFamily: "'Oswald', sans-serif" }}>Weight</p>
                <p className="text-xs font-bold text-[#2a0a3d]" style={{ fontFamily: "'Oswald', sans-serif" }}>{player.weight}</p>
              </div>
              <div className="bg-gray-50 rounded-md px-2 py-1.5 col-span-2">
                <p className="text-[10px] text-gray-400 uppercase tracking-wider" style={{ fontFamily: "'Oswald', sans-serif" }}>College</p>
                <p className="text-xs font-bold text-[#2a0a3d]" style={{ fontFamily: "'Oswald', sans-serif" }}>{player.college}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Back */}
        <div
          className="absolute inset-0 rounded-lg overflow-hidden bg-gradient-to-br from-[#4a1a6b] to-[#2a0a3d] p-5 flex flex-col justify-between"
          style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
        >
          <div>
            <div className="flex items-center justify-between mb-4">
              <span className={`px-2 py-1 rounded-md text-xs font-black tracking-widest uppercase ${badgeClass}`} style={{ fontFamily: "'Oswald', sans-serif" }}>
                {player.position}
              </span>
              <span className="text-[#ffd700]/40 text-4xl font-black leading-none" style={{ fontFamily: "'Oswald', sans-serif" }}>
                #{player.number}
              </span>
            </div>
            <h3 className="text-xl font-black text-white mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>
              {player.name}
            </h3>
            <p className="text-[#ffd700] text-xs tracking-widest uppercase mb-4" style={{ fontFamily: "'Oswald', sans-serif" }}>
              {player.experience} · {player.hometown}
            </p>
            <p className="text-white/70 text-xs leading-relaxed">{player.bio}</p>
          </div>

          <div className="border-t border-white/10 pt-4 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-white/40 text-[10px] uppercase tracking-wider mb-0.5" style={{ fontFamily: "'Oswald', sans-serif" }}>Experience</p>
                <p className="text-white text-xs font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>{player.experience}</p>
              </div>
              <div>
                <p className="text-white/40 text-[10px] uppercase tracking-wider mb-0.5" style={{ fontFamily: "'Oswald', sans-serif" }}>College</p>
                <p className="text-white text-xs font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>{player.college}</p>
              </div>
            </div>
            <p className="text-white/30 text-[10px] mt-3 text-center" style={{ fontFamily: "'Oswald', sans-serif" }}>Click to flip back</p>
          </div>
        </div>
      </div>
    </div>
  );
}
